<script setup>
// Full-page single-unit presentation view, matching docs/addon/e.inventory2.png:
// back/breadcrumb header, left = zoomable individual floor plan, right =
// unit header + stat row + Details/Specifications tabs. Opened from the
// Inventory tab's floor plan / units table. Scheduling reuses the exact
// same SiteVisitForm modal the Calendar/Kanban pages already use
// (Nexceo::SiteVisit, lead-based) instead of inventing a unit-level
// scheduling flow; "Request More Information" reuses the existing
// lead-creation page instead of a second lead pipeline.
//
// Per explicit instruction, only "Details" and "Specifications" tabs are
// shown — the reference design's Location/Price Breakup/Amenities tabs are
// dropped. Specifications render Nexceo::UnitInventory#specifications
// (room label + size pairs, admin-entered on the Inventory page, stored in
// the unit's existing metadata jsonb — see UnitInventoriesController).
//
// Note on the reference design: it also shows "Add to Favorites", a
// bathroom count, "Common Charges", "Government Tax", "RERA Carpet Area",
// an "Identify" viewer mode and a building-orientation compass. None of
// that exists on Nexceo::UnitInventory or anywhere else in the schema (or,
// for the compass, isn't verifiable real data), so none of it is
// fabricated here.
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SiteVisitForm from '../../calendar/SiteVisitForm.vue';

const props = defineProps({
  unit: { type: Object, required: true },
  project: { type: Object, required: true },
});
defineEmits(['close']);

const router = useRouter();
const { accountScopedRoute } = useAccount();

const showScheduleModal = ref(false);
const activeTab = ref('details');

// Canonical status colors — must match ProjectInventory.vue's
// STATUS_OPTIONS exactly (that admin page is the single source of truth).
const STATUS_META = {
  available: { label: 'Available', hex: '#10b981' },
  hold: { label: 'On Hold', hex: '#f59e0b' },
  blocked: { label: 'Blocked', hex: '#f97316' },
  sold: { label: 'Sold', hex: '#ef4444' },
  unavailable: { label: 'Unavailable', hex: '#94a3b8' },
  landowner: { label: 'Landowner', hex: '#8b5cf6' },
};
function statusMeta(unit) {
  return STATUS_META[unit.status] ?? { label: unit.status, hex: '#64748b' };
}

function floorLabel(floor) {
  if (floor === null || floor === undefined) return null;
  if (floor === 0) return 'Ground';
  const suffixes = { 1: 'st', 2: 'nd', 3: 'rd' };
  const suffix = suffixes[floor % 10] && floor % 100 < 11 ? suffixes[floor % 10] : 'th';
  return `${floor}${suffix} Floor`;
}

// Derived, not fabricated: bhk is a real field (e.g. "3 BHK"); the leading
// number is a reasonable bedroom count to surface without inventing a
// separate column. Falls back to null rather than guessing when unparsable.
const bedroomCount = computed(() => {
  const match = String(props.unit.bhk || '').match(/\d+/);
  return match ? match[0] : null;
});

function formatArea(value, unitLabel) {
  return value ? `${Number(value).toLocaleString()} ${unitLabel}` : null;
}

function formatPrice(unit) {
  if (unit.price_display) return unit.price_display;
  const v = Number(unit.price);
  if (!v) return null;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  return `₹${v.toLocaleString()}`;
}

const detailRows = computed(() => {
  const u = props.unit;
  const rows = [{ label: 'Unit Number', value: u.unit_no }];
  const towerFloor = [u.tower && `Tower ${u.tower}`, floorLabel(u.floor)]
    .filter(Boolean)
    .join(' - ');
  if (towerFloor) rows.push({ label: 'Tower / Floor', value: towerFloor });
  if (u.bhk) rows.push({ label: 'Configuration', value: u.bhk });
  if (formatArea(u.super_builtup_area, 'sq ft'))
    rows.push({
      label: 'Saleable Area (SBA)',
      value: formatArea(u.super_builtup_area, 'sq ft'),
    });
  if (formatArea(u.carpet_area, 'sq ft'))
    rows.push({ label: 'Carpet Area', value: formatArea(u.carpet_area, 'sq ft') });
  if (formatArea(u.builtup_area, 'sq ft'))
    rows.push({ label: 'Built-up Area', value: formatArea(u.builtup_area, 'sq ft') });
  if (formatArea(u.balcony_area, 'sq ft'))
    rows.push({ label: 'Balcony Area', value: formatArea(u.balcony_area, 'sq ft') });
  if (u.facing) rows.push({ label: 'Facing', value: `${u.facing} Facing` });
  rows.push({ label: 'Status', value: null, isStatus: true });
  if (formatPrice(u)) rows.push({ label: 'Price', value: formatPrice(u) });
  return rows;
});

function requestMoreInfo() {
  router.push(
    accountScopedRoute('nexceo_leads_new', undefined, {
      project_id: props.project.id,
    })
  );
}

async function shareUnit() {
  try {
    await navigator.clipboard.writeText(window.location.href);
    useAlert('Link copied to clipboard.');
  } catch {
    useAlert('Could not copy link — please copy the URL from the address bar.');
  }
}

// ── Floor plan zoom / fullscreen (docs/addon/e.inventory2.png control
// cluster) — same requestFullscreen/fullscreenchange pattern as
// MapsTab.vue's toolbar fullscreen button, so Escape-to-exit stays in sync.
const zoomScale = ref(1);
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
function zoomIn() {
  zoomScale.value = Math.min(MAX_ZOOM, Math.round((zoomScale.value + 0.25) * 100) / 100);
}
function zoomOut() {
  zoomScale.value = Math.max(MIN_ZOOM, Math.round((zoomScale.value - 0.25) * 100) / 100);
}
function resetZoom() {
  zoomScale.value = 1;
}

const floorPlanContainerRef = ref(null);
const isFullscreen = ref(false);
function onFullscreenChange() {
  isFullscreen.value = document.fullscreenElement === floorPlanContainerRef.value;
}
function toggleFullscreen() {
  if (document.fullscreenElement) {
    document.exitFullscreen();
  } else {
    floorPlanContainerRef.value?.requestFullscreen();
  }
}

// Natural aspect ratio of the unit's floor plan image, captured on load so
// the viewer box can match it exactly (no letterboxing, fits without a
// scrollbar at 100% zoom).
const imgAspect = ref(null);
function onFloorPlanImageLoad(e) {
  imgAspect.value = e.target.naturalWidth / e.target.naturalHeight;
}
watch(
  () => props.unit.id,
  () => {
    imgAspect.value = null;
    zoomScale.value = 1;
  }
);

onMounted(() => {
  document.addEventListener('fullscreenchange', onFullscreenChange);
});
onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', onFullscreenChange);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <teleport to="body">
    <div class="fixed inset-0 z-50 flex flex-col bg-n-background">
      <!-- Top bar -->
      <div
        class="flex items-center justify-between gap-3 px-5 py-3 border-b border-n-weak shrink-0 flex-wrap"
      >
        <div class="flex items-center gap-3 min-w-0">
          <button
            class="flex items-center justify-center size-8 rounded-lg text-n-slate-10 hover:bg-n-alpha-2 transition-colors shrink-0"
            @click="$emit('close')"
          >
            <span class="i-lucide-arrow-left size-4" />
          </button>
          <div class="flex flex-col min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-base font-bold text-n-slate-12 truncate">
                {{ unit.unit_no }}
              </span>
              <span
                class="text-xs font-medium px-2 py-0.5 rounded-full shrink-0"
                :style="{
                  backgroundColor: `${statusMeta(unit).hex}22`,
                  color: statusMeta(unit).hex,
                }"
              >
                {{ statusMeta(unit).label }}
              </span>
            </div>
            <p class="flex items-center gap-1 text-xs font-medium text-n-slate-9 truncate">
              {{ project.name }}
              <span class="i-lucide-chevron-right size-3 shrink-0" />
              <template v-if="unit.tower">
                Tower {{ unit.tower
                }}<span class="i-lucide-chevron-right size-3 shrink-0" />
              </template>
              <template v-if="floorLabel(unit.floor)">
                {{ floorLabel(unit.floor)
                }}<span class="i-lucide-chevron-right size-3 shrink-0" />
              </template>
              Unit {{ unit.unit_no }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg !border !border-solid border-woot-500 text-xs font-medium text-woot-600 dark:text-woot-400 hover:bg-woot-50 dark:hover:bg-woot-500/10 transition-colors"
            @click="showScheduleModal = true"
          >
            <span class="i-lucide-calendar-plus size-3.5" />
            Schedule Site Visit
          </button>
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-woot-500 text-white text-xs font-medium hover:bg-woot-600 transition-colors"
            @click="requestMoreInfo"
          >
            <span class="i-lucide-help-circle size-3.5" />
            Request More Information
          </button>
        </div>
      </div>

      <!-- Sub-tab (single mode — no "Identify" overlay data exists) -->
      <div class="flex items-center gap-4 px-5 border-b border-n-weak shrink-0">
        <span
          class="py-2.5 text-xs font-semibold border-b-2 border-woot-500 text-woot-600 dark:text-woot-400"
        >
          Floor Plan
        </span>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto p-5">
        <div class="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5 items-start">
          <!-- Left: zoomable individual floor plan -->
          <div
            ref="floorPlanContainerRef"
            class="relative rounded-xl border border-n-weak bg-n-background overflow-auto flex items-center justify-center h-[50vh] lg:h-[calc(100vh-180px)]"
            :class="isFullscreen ? '!h-screen' : ''"
          >
            <template v-if="unit.floor_plan_image_url">
              <div
                class="relative shrink-0 transition-transform duration-150"
                :style="{
                  height: '100%',
                  aspectRatio: imgAspect || 3 / 4,
                  transform: `scale(${zoomScale})`,
                }"
              >
                <img
                  :src="unit.floor_plan_image_url"
                  :alt="`${unit.unit_no} floor plan`"
                  class="w-full h-full block"
                  @load="onFloorPlanImageLoad"
                />
              </div>
            </template>
            <div v-else class="flex flex-col items-center gap-2 py-16 text-center px-6">
              <span class="i-lucide-image-off size-8 text-n-slate-6" />
              <p class="text-sm font-medium text-n-slate-9">
                No individual floor plan uploaded for this unit yet.
              </p>
            </div>

            <!-- Zoom / fullscreen controls (top-left, per e.inventory2.png) -->
            <div
              v-if="unit.floor_plan_image_url"
              class="absolute top-3 left-3 flex flex-col gap-1.5 z-10"
            >
              <button
                type="button"
                class="flex items-center justify-center size-8 rounded-lg bg-n-background !border !border-solid border-n-weak shadow-sm text-n-slate-11 hover:bg-n-alpha-2 transition-colors disabled:opacity-40"
                :disabled="zoomScale >= MAX_ZOOM"
                title="Zoom in"
                @click="zoomIn"
              >
                <span class="i-lucide-plus size-4" />
              </button>
              <button
                type="button"
                class="flex items-center justify-center size-8 rounded-lg bg-n-background !border !border-solid border-n-weak shadow-sm text-n-slate-11 hover:bg-n-alpha-2 transition-colors disabled:opacity-40"
                :disabled="zoomScale <= MIN_ZOOM"
                title="Zoom out"
                @click="zoomOut"
              >
                <span class="i-lucide-minus size-4" />
              </button>
              <button
                type="button"
                class="flex items-center justify-center size-8 rounded-lg bg-n-background !border !border-solid border-n-weak shadow-sm text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                title="Fullscreen"
                @click="toggleFullscreen"
              >
                <span class="i-lucide-maximize size-4" />
              </button>
              <button
                type="button"
                class="flex items-center justify-center size-8 rounded-lg bg-n-background !border !border-solid border-n-weak shadow-sm text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                title="Reset zoom"
                @click="resetZoom"
              >
                <span class="i-lucide-rotate-ccw size-4" />
              </button>
            </div>
          </div>

          <!-- Right: header, stats, tabs -->
          <div class="flex flex-col gap-4">
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2">
                <span class="text-lg font-bold text-n-slate-12">{{ unit.unit_no }}</span>
                <span
                  class="text-xs font-medium px-2 py-0.5 rounded-full"
                  :style="{
                    backgroundColor: `${statusMeta(unit).hex}22`,
                    color: statusMeta(unit).hex,
                  }"
                >
                  {{ statusMeta(unit).label }}
                </span>
              </div>
              <button
                class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg !border !border-solid border-n-weak text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                @click="shareUnit"
              >
                <span class="i-lucide-share-2 size-3.5" />
                Share
              </button>
            </div>
            <p class="flex items-center gap-1.5 text-xs font-medium text-n-slate-9 -mt-2">
              <template v-if="unit.tower">Tower {{ unit.tower }}</template>
              <span v-if="unit.tower && floorLabel(unit.floor)">|</span>
              <template v-if="floorLabel(unit.floor)">{{ floorLabel(unit.floor) }}</template>
              <span v-if="unit.bhk">|</span>
              <template v-if="unit.bhk">{{ unit.bhk }}</template>
            </p>

            <!-- Stat row -->
            <div class="flex items-center gap-5 rounded-xl border border-n-weak px-4 py-3 flex-wrap">
              <div v-if="unit.super_builtup_area" class="flex items-center gap-2">
                <span class="i-lucide-ruler size-4 text-woot-500 shrink-0" />
                <div class="leading-tight">
                  <p class="text-sm font-bold text-n-slate-12">
                    {{ Number(unit.super_builtup_area).toLocaleString() }}
                  </p>
                  <p class="text-[10px] font-medium text-n-slate-9">sq ft (SBA)</p>
                </div>
              </div>
              <div v-if="bedroomCount" class="flex items-center gap-2">
                <span class="i-lucide-bed-double size-4 text-woot-500 shrink-0" />
                <div class="leading-tight">
                  <p class="text-sm font-bold text-n-slate-12">{{ bedroomCount }}</p>
                  <p class="text-[10px] font-medium text-n-slate-9">Bedroom(s)</p>
                </div>
              </div>
              <div v-if="floorLabel(unit.floor)" class="flex items-center gap-2">
                <span class="i-lucide-layers size-4 text-woot-500 shrink-0" />
                <div class="leading-tight">
                  <p class="text-sm font-bold text-n-slate-12">{{ floorLabel(unit.floor) }}</p>
                  <p class="text-[10px] font-medium text-n-slate-9">Floor</p>
                </div>
              </div>
            </div>

            <!-- Price -->
            <div
              v-if="formatPrice(unit)"
              class="rounded-xl bg-woot-50 dark:bg-woot-500/10 p-4"
            >
              <p class="text-xs font-medium text-n-slate-9">Price</p>
              <p class="text-lg font-bold text-woot-700 dark:text-woot-400">
                {{ formatPrice(unit) }}
              </p>
            </div>

            <!-- Tabs -->
            <div class="flex items-center gap-4 border-b border-n-weak">
              <button
                type="button"
                class="py-2.5 text-xs font-semibold !border-b-2 !border-solid transition-colors"
                :class="
                  activeTab === 'details'
                    ? 'border-woot-500 text-woot-600 dark:text-woot-400'
                    : 'border-transparent text-n-slate-9 hover:text-n-slate-11'
                "
                @click="activeTab = 'details'"
              >
                Details
              </button>
              <button
                type="button"
                class="py-2.5 text-xs font-semibold !border-b-2 !border-solid transition-colors"
                :class="
                  activeTab === 'specifications'
                    ? 'border-woot-500 text-woot-600 dark:text-woot-400'
                    : 'border-transparent text-n-slate-9 hover:text-n-slate-11'
                "
                @click="activeTab = 'specifications'"
              >
                Specifications
              </button>
            </div>

            <template v-if="activeTab === 'details'">
              <div class="rounded-xl border border-n-weak overflow-hidden">
                <div
                  v-for="row in detailRows"
                  :key="row.label"
                  class="flex items-center justify-between gap-3 px-4 py-2.5 text-sm border-b border-n-weak last:border-0 odd:bg-n-alpha-1"
                >
                  <span class="font-medium text-n-slate-9">{{ row.label }}</span>
                  <span
                    v-if="row.isStatus"
                    class="text-xs font-medium px-2 py-0.5 rounded-full"
                    :style="{
                      backgroundColor: `${statusMeta(unit).hex}22`,
                      color: statusMeta(unit).hex,
                    }"
                  >
                    {{ statusMeta(unit).label }}
                  </span>
                  <span v-else class="font-semibold text-n-slate-12">{{ row.value }}</span>
                </div>
              </div>

              <p v-if="unit.notes" class="text-sm text-n-slate-11 mt-3">
                {{ unit.notes }}
              </p>
            </template>

            <template v-else-if="activeTab === 'specifications'">
              <div v-if="unit.specifications?.length" class="flex flex-col gap-2">
                <div
                  v-for="(spec, i) in unit.specifications"
                  :key="i"
                  class="flex items-center justify-between gap-3 rounded-lg border border-n-weak px-3 py-2"
                >
                  <span class="text-sm font-medium text-n-slate-10">{{ spec.label }}</span>
                  <span class="text-sm font-semibold text-n-slate-12">{{
                    spec.size
                  }}</span>
                </div>
              </div>
              <p v-else class="text-sm text-n-slate-9">
                Specifications for this unit haven't been added yet.
              </p>
            </template>
          </div>
        </div>
      </div>
    </div>

    <SiteVisitForm
      :is-open="showScheduleModal"
      :prefill-project="project"
      prefill-visit-type="site_visit"
      @close="showScheduleModal = false"
      @saved="showScheduleModal = false"
    />
  </teleport>
</template>
